// Technical Staff Review Modal

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getAdminId } from '@/utils/auth';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { toast } from 'sonner';

const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $notes: String,
      $acceptedByTechnicalStaff: Boolean,
      $assignedTechnicalStaffId: ID,
      $assignedTechnicalStaffName: String,
      $awaitingOOP: Boolean,
      $approvedByTechnicalStaff: Boolean,
      $awaitingPermitCreation: Boolean,
      $hasInspectionReport: Boolean
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         notes: $notes,
         acceptedByTechnicalStaff: $acceptedByTechnicalStaff,
         assignedTechnicalStaffId: $assignedTechnicalStaffId,
         assignedTechnicalStaffName: $assignedTechnicalStaffName,
         awaitingOOP: $awaitingOOP,
         approvedByTechnicalStaff: $approvedByTechnicalStaff,
         awaitingPermitCreation: $awaitingPermitCreation,
         hasInspectionReport: $hasInspectionReport
      ) {
         id
         currentStage
         status
         requestType
         acceptedByTechnicalStaff
         assignedTechnicalStaffId
         assignedTechnicalStaffName
         assignmentDate
         approvedByTechnicalStaff
         awaitingPermitCreation
         hasInspectionReport
      }
   }
`;

const GET_PLTP_PERMIT = gql`
   query GetPLTPPermit($id: ID!) {
      getPLTPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         status
         currentStage
      }
   }
`;

const GET_TCEBP_PERMIT = gql`
   query GetTCEBPPermit($id: ID!) {
      getTCEBPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         status
         currentStage
      }
   }
`;

const GET_PLTCP_PERMIT = gql`
   query GetPLTCPPermit($id: ID!) {
      getPLTCPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         status
         currentStage
      }
   }
`;

const TS_ReviewModal = ({ isOpen, onClose, application, onReviewComplete }) => {
   const [isReturning, setIsReturning] = useState(false);
   const [remarks, setRemarks] = useState('');
   const [updatePermitStage] = useMutation(UPDATE_PERMIT_STAGE);
   const [isLoading, setIsLoading] = useState(false);

   // #region
   const { data: pltpData } = useQuery(GET_PLTP_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Private Land Timber Permit',
   });

   const { data: tcebpData } = useQuery(GET_TCEBP_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Tree Cutting and/or Earth Balling Permit',
   });

   const { data: pltcpData } = useQuery(GET_PLTCP_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Public Land Tree Cutting Permit',
   });

   const isCuttingRequest = (
      pltpData?.getPLTPPermitById?.requestType === 'Cutting' ||
      (tcebpData?.getTCEBPPermitById?.requestType === 'Cutting' || tcebpData?.getTCEBPPermitById?.requestType === 'Earth Balling') ||
      pltcpData?.getPLTCPPermitById?.requestType === 'Cutting'
   );

   const isInventoryRequest = (
      pltpData?.getPLTPPermitById?.requestType === 'Inventory' ||
      tcebpData?.getTCEBPPermitById?.requestType === 'Inventory' ||
      pltcpData?.getPLTCPPermitById?.requestType === 'Inventory'
   );
   // #endregion

   const handleAccept = async () => {
      try {
         if (!application?.id) throw new Error('Invalid application data');

         const adminId = getAdminId();
         console.log('Admin ID:', adminId);
         if (!adminId) throw new Error('Technical staff not authenticated');

         // Get admin details from localStorage
         // const admin = getStaffName();
         const admin = JSON.parse(localStorage.getItem('admin'));
         if (!admin) throw new Error('Admin details not found');

         const isPrivateTreePlantation = () => {
            if (application.applicationType === 'Private Tree Plantation Registration') {
               return true;
            } else {
               return false;
            }
         };

         const isCOV = application.applicationType === 'Certificate of Verification';

         const isChainsawRegistration = application.applicationType === 'Chainsaw Registration';

         // Show immediate feedback to user
         toast.info('Processing application, please wait...');

         // Set loading state
         setIsLoading(true);

         // Close the modal immediately to improve perceived performance
         onClose();

         if (isChainsawRegistration) {
            await handleCSAWAccept();
            return;
         }

         if (isCuttingRequest) {
            await handleCuttingReqAccept();
            return;
         }

         await updatePermitStage({
            variables: {
               id: application.id,
               currentStage: 'ForInspectionByTechnicalStaff',
               status: 'In Progress',
               notes: 'Application accepted by Technical Staff',
               acceptedByTechnicalStaff: true,
               assignedTechnicalStaffId: adminId,
               assignedTechnicalStaffName: `${admin.firstName} ${admin.lastName}`,
               // if application type is CSAW then await OOP
               // if PTPR then await OOP is false(PTPR does not require OOP)
               // if COV, PLTCP, PLTP, TCEBP then await OOP is false
               awaitingOOP: !isPrivateTreePlantation() && !isInventoryRequest && !isCOV
            }
         });

         onReviewComplete();
         toast.success('Application accepted and assigned successfully');
      } catch (error) {
         console.error('Error accepting application:', error);
         toast.error(`Error accepting application: ${error.message}`);
      } finally {
         setIsLoading(false);
      }
   };

   const handleCSAWAccept = async () => {
      try {
         const adminId = getAdminId();
         console.log('Admin ID:', adminId);
         if (!adminId) throw new Error('Technical staff not authenticated');

         const admin = JSON.parse(localStorage.getItem('admin'));
         if (!admin) throw new Error('Admin details not found');

         // Set loading state
         setIsLoading(true);

         const result = await updatePermitStage({
            variables: {
               id: application.id,
               currentStage: 'ChiefRPSReview',
               status: 'In Progress',
               notes: 'Application accepted by Technical Staff',
               acceptedByTechnicalStaff: true,
               assignedTechnicalStaffId: adminId,
               assignedTechnicalStaffName: `${admin.firstName} ${admin.lastName}`,
               awaitingOOP: true
            }
         });

         onReviewComplete();
         toast.success('Application accepted and assigned successfully');
      } catch (error) {
         console.error('Error accepting Chainsaw Registration:', error);
         toast.error(`Error accepting Chainsaw Registration: ${error.message}`);
      } finally {
         setIsLoading(false);
      }
   };

   const handleCuttingReqAccept = async () => {
      // oop first then approve authenticity
      try {
         const adminId = getAdminId();
         console.log('Admin ID:', adminId);
         if (!adminId) throw new Error('Technical staff not authenticated');

         // Get admin details from localStorage
         const admin = JSON.parse(localStorage.getItem('admin'));
         if (!admin) throw new Error('Admin details not found');

         // Set loading state
         setIsLoading(true);

         // Check if this is an earth balling request (TCEBP permit with Cutting or Earth Balling request type)
         const isEarthBallingRequest = application?.applicationType === 'Tree Cutting and/or Earth Balling Permit' &&
            (tcebpData?.getTCEBPPermitById?.requestType === 'Earth Balling' ||
            tcebpData?.getTCEBPPermitById?.requestType === 'Cutting');

         await updatePermitStage({
            variables: {
               id: application.id,
               // currentStage: 'ChiefRPSReview',
               currentStage: 'ForInspectionByTechnicalStaff', // using this so the application can be at the For Inspection and Approval tab
               status: 'In Progress',
               notes: 'Application accepted by Technical Staff',
               // approvedByTechnicalStaff: true,
               // awaitingPermitCreation: true, // direct to Permit Creation if request is cutting/earth balling

               acceptedByTechnicalStaff: true,
               awaitingOOP: true,
               assignedTechnicalStaffId: adminId,
               assignedTechnicalStaffName: `${admin.firstName} ${admin.lastName}`,
               // awaitingOOP: true,

               // Automatically set hasInspectionReport to true for earth balling/cutting requests
               // since inspection was already done during inventory
               hasInspectionReport: isEarthBallingRequest ? true : undefined
            }
         });

         onReviewComplete();
         toast.success('Application accepted and assigned successfully');
      } catch (error) {
         console.error('Error accepting cutting request:', error);
         toast.error(`Error accepting cutting request: ${error.message}`);
      } finally {
         setIsLoading(false);
      }
   }

   const handleReturn = async () => {
      if (!remarks.trim()) {
         toast.error('Please provide remarks for returning the application.');
         return;
      }
      const adminId = getAdminId();
      console.log('Admin ID:', adminId);
      if (!adminId) throw new Error('Technical staff not authenticated');

      // Get admin details from localStorage
      const admin = JSON.parse(localStorage.getItem('admin'));
      if (!admin) throw new Error('Admin details not found');

      try {
         if (!application?.id) throw new Error('Invalid application data');

         await updatePermitStage({
            variables: {
               id: application.id,
               currentStage: 'ReturnedByTechnicalStaff',
               status: 'Returned',
               notes: remarks,
               acceptedByTechnicalStaff: false,
               assignedTechnicalStaffId: adminId,
               assignedTechnicalStaffName: `${admin.firstName} ${admin.lastName}`,
               // approvedByTechnicalStaff will be undefined
            }
         });

         onReviewComplete();
         onClose();
         toast.success('Application returned successfully');
      } catch (error) {
         console.error('Error returning application:', error);
         toast.error(`Error returning application: ${error.message}`);
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="max-w-md">
            <DialogHeader>
               <DialogTitle>Review Application</DialogTitle>
               <DialogDescription>
                  Application Number: {application?.applicationNumber}
               </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
               {isReturning ? (
                  <>
                     <textarea
                        data-testid="return-remarks"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                        placeholder="Enter remarks for returning the application"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                     />
                     <Button
                        data-testid="confirm-return"
                        onClick={handleReturn}
                        className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                        disabled={!remarks.trim() || isLoading}
                     >
                        {isLoading ? 'Processing...' : 'Confirm Return'}
                     </Button>
                     <Button
                        data-testid="cancel-return"
                        onClick={() => setIsReturning(false)}
                        variant="outline"
                        className="w-full"
                        disabled={isLoading}
                     >
                        Cancel
                     </Button>
                  </>
               ) : (
                  <>
                     <Button
                        data-testid="accept-button"
                        onClick={handleAccept}
                        className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                        disabled={isLoading}
                     >
                        {isLoading ? 'Processing...' : 'Accept'}
                     </Button>
                     <Button
                        data-testid="return-button"
                        onClick={() => setIsReturning(true)}
                        variant="outline"
                        className="w-full"
                        disabled={isLoading}
                     >
                        Return
                     </Button>
                  </>
               )}
            </div>
         </DialogContent>
      </Dialog>
   );
};

export default TS_ReviewModal;
